import { useMutation } from '@apollo/client'
import { Formik } from 'formik'
import { ReactElement, useEffect, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'

import { Input } from 'components/Input'
import Snackbar from 'components/Snackbar'
import { CHANGE_ROUTE } from 'features/Routes/mutations-legacy'

const RouteNameInput = styled(Input)`
  width: 120px;
  display: block;
`

const messages = defineMessages({
  routeNamePlaceholder: {
    id: 'routes.list.routeName.placeholder',
    defaultMessage: 'Enter route name'
  }
})

interface IRouteNameFormProps {
  routeId: string
  routeName: string
}

interface IRouteNameValues {
  name: string
}

interface IRouteNameErrorNotificationProps {
  onClose: () => void
}

const RouteNameErrorNotification = ({ onClose }: IRouteNameErrorNotificationProps): ReactElement => {
  useEffect(() => {
    setTimeout(() => {
      onClose()
    }, 5 * 1000)
  }, [])

  return (
    <Snackbar.Container>
      <Snackbar.Content>
        <FormattedMessage id="routes.list.name.error.message" defaultMessage="The route name couldn't be saved." />
      </Snackbar.Content>
      <Snackbar.Action onClick={onClose}>
        <FormattedMessage id="routes.list.name.error.action" defaultMessage="Close" />
      </Snackbar.Action>
    </Snackbar.Container>
  )
}

export const RouteNameForm = ({ routeId, routeName }: IRouteNameFormProps): ReactElement => {
  const intl = useIntl()

  const [isShowingErrorNotification, showErrorNotification] = useState(false)
  const [changeRoute, { loading }] = useMutation(CHANGE_ROUTE, {
    onError(): void {
      showErrorNotification(true)
    }
  })
  const initialValues: IRouteNameValues = {
    name: routeName
  }

  const handleSubmit = (values: IRouteNameValues): void => {
    changeRoute({
      variables: {
        id: routeId,
        input: {
          name: values.name
        }
      }
    })
  }

  return (
    <>
      <Formik initialValues={initialValues} onSubmit={handleSubmit}>
        {({ values, handleChange, handleBlur, handleSubmit }): ReactElement => {
          return (
            <form onSubmit={handleSubmit}>
              <RouteNameInput
                name="name"
                value={values.name}
                placeholder={intl.formatMessage(messages.routeNamePlaceholder)}
                onChange={handleChange}
                onBlur={handleBlur}
                small
                loading={loading}
              />
            </form>
          )
        }}
      </Formik>
      {isShowingErrorNotification && <RouteNameErrorNotification onClose={(): void => showErrorNotification(false)} />}
    </>
  )
}
